var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { DocumentRetrievalAuthType, EpicIntegration } from "healthex-common/document.retrieval.dtos";
import { asEnum } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { NamedRoutes } from "./NamedRoutes";
export var FacilitatedFHIRAuthState;
(function (FacilitatedFHIRAuthState) {
    FacilitatedFHIRAuthState["INACTIVE"] = "INACTIVE";
    FacilitatedFHIRAuthState["IN_PROGRESS"] = "IN_PROGRESS";
    FacilitatedFHIRAuthState["SUCCESS"] = "SUCCESS";
    FacilitatedFHIRAuthState["ERROR"] = "ERROR";
})(FacilitatedFHIRAuthState || (FacilitatedFHIRAuthState = {}));
export var FacilitatedFHIRAuthErrorReason;
(function (FacilitatedFHIRAuthErrorReason) {
    FacilitatedFHIRAuthErrorReason["AUTH_FAILED"] = "AUTH_FAILED";
    FacilitatedFHIRAuthErrorReason["WRONG_STATE"] = "WRONG_STATE";
    FacilitatedFHIRAuthErrorReason["NO_AUTH_DATA"] = "NO_AUTH_DATA";
    FacilitatedFHIRAuthErrorReason["INSUFFICIENT_SCOPE"] = "INSUFFICIENT_SCOPE";
})(FacilitatedFHIRAuthErrorReason || (FacilitatedFHIRAuthErrorReason = {}));
export class FacilitatedFHIRAuthManager {
    static applyCallbackRedirect() {
        const { search, hash } = window.location;
        if (hash) {
            return false;
        }
        const state = new URLSearchParams(search).get("state");
        if (state === null || state === void 0 ? void 0 : state.startsWith("FACILITATED_FHIR_ECW_")) {
            window.location.hash = "#/ecw/redirect";
            return false;
        }
        if (state === null || state === void 0 ? void 0 : state.startsWith("FACILITATED_FHIR_ORACLE_")) {
            window.location.hash = "#/oracle/redirect";
            return false;
        }
        return false;
    }
    constructor(persistence, routingUIManager, environment) {
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.environment = environment;
        this.state = { state: FacilitatedFHIRAuthState.INACTIVE };
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if ((currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.FACILITATED_FHIR_CALLBACK.routeName ||
                (currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.FACILITATED_FHIR_OPEN_EPIC_CALLBACK.routeName ||
                (currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.FACILITATED_FHIR_ECW_CALLBACK.routeName ||
                (currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.FACILITATED_FHIR_ORACLE_CALLBACK.routeName) {
                void this.processFacilitatedFHIRAuthCallback();
            }
            else if (currentRoute !== null) {
                this.resetAuthStateIfNeeded();
            }
        }, { fireImmediately: true, name: "FacilitatedFHIRAuthManager_currentRoute" });
        window.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                this.resetAuthStateIfNeeded();
            }
        });
    }
    get hasCompletedAuth() {
        return this.state.state === FacilitatedFHIRAuthState.SUCCESS || this.state.state === FacilitatedFHIRAuthState.ERROR;
    }
    redirectToFacilitatedFHIRLoginPage(input) {
        this.state = { state: FacilitatedFHIRAuthState.IN_PROGRESS, key: input.key, fhirEndpoint: input.endpoint.fhirEndpoint };
        this.setFacilitatedFHIRAuthData({
            key: input.key,
            state: input.state,
            callbackHash: input.callbackHash,
            fhirEndpoint: input.endpoint.fhirEndpoint,
            endpoint: input.endpoint,
            redirectUri: input.redirectUri,
            codeVerifier: input.codeVerifier,
        });
        if (input.method === "POST" && input.formFields) {
            const form = document.createElement("form");
            form.method = "POST";
            form.action = input.url;
            form.style.display = "none";
            for (const [name, value] of Object.entries(input.formFields)) {
                const field = document.createElement("input");
                field.type = "hidden";
                field.name = name;
                field.value = value;
                form.appendChild(field);
            }
            document.body.appendChild(form);
            form.submit();
            return;
        }
        if (input.method === "GET" && input.formFields) {
            const url = new URL(input.url);
            for (const [name, value] of Object.entries(input.formFields)) {
                url.searchParams.set(name, value);
            }
            window.location.href = url.toString();
            return;
        }
        window.location.href = input.url;
    }
    async processFacilitatedFHIRAuthCallback() {
        const data = new URLSearchParams(window.location.search);
        const code = data.get("code");
        const state = data.get("state");
        const error = data.get("error");
        try {
            const authData = this.getFacilitatedFHIRAuthData();
            if (!authData) {
                this.onError(FacilitatedFHIRAuthErrorReason.NO_AUTH_DATA);
                return;
            }
            if (error) {
                this.onError(FacilitatedFHIRAuthErrorReason.AUTH_FAILED, authData === null || authData === void 0 ? void 0 : authData.key, authData === null || authData === void 0 ? void 0 : authData.fhirEndpoint);
            }
            else if (code) {
                if (authData.state !== state) {
                    this.onError(FacilitatedFHIRAuthErrorReason.WRONG_STATE, authData === null || authData === void 0 ? void 0 : authData.key, authData === null || authData === void 0 ? void 0 : authData.fhirEndpoint);
                    return;
                }
                this.onSuccess(authData, code);
            }
            else {
                this.onError(FacilitatedFHIRAuthErrorReason.AUTH_FAILED, authData === null || authData === void 0 ? void 0 : authData.key, authData === null || authData === void 0 ? void 0 : authData.fhirEndpoint);
            }
        }
        finally {
            window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
        }
    }
    getRedirectUri(integration) {
        let origin = window.location.origin;
        if (integration === EpicIntegration.EPIC_NEXUS) {
            if (origin.includes("app.demo.healthex.dev"))
                origin = "https://app.healthex.dev";
        }
        const hashPath = integration === EpicIntegration.OPEN_EPIC ? "open-epic/redirect" : "epic-nexus/redirect";
        return `${origin}/#/${hashPath}`;
    }
    getRedirectUriForEndpoint(endpoint) {
        var _a;
        if (endpoint.authType === DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC) {
            const integrationValue = endpoint.vendorContext.epicIntegration;
            const integration = typeof integrationValue === "string"
                ? (_a = asEnum(EpicIntegration)(integrationValue)) !== null && _a !== void 0 ? _a : EpicIntegration.OPEN_EPIC
                : EpicIntegration.OPEN_EPIC;
            return this.getRedirectUri(integration);
        }
        if (endpoint.authType === DocumentRetrievalAuthType.FACILITATED_FHIR_ECW) {
            if (window.location.hostname === "localhost") {
                const callbackBase = this.environment.PUBLIC_CALLBACK_BASE_URL || this.environment.API_BASE_URL;
                return callbackBase.replace(/\/+$/, "");
            }
            return window.location.origin;
        }
        if (endpoint.authType === DocumentRetrievalAuthType.FACILITATED_FHIR_ORACLE) {
            return window.location.origin;
        }
        return `${window.location.origin}/#/epic-nexus/redirect`;
    }
    resetAuth() {
        this.state = { state: FacilitatedFHIRAuthState.INACTIVE };
    }
    handleAuthError(error, key) {
        this.onError(error, key);
    }
    onError(error, key, fhirEndpoint) {
        this.state = { state: FacilitatedFHIRAuthState.ERROR, key: key !== null && key !== void 0 ? key : "", error, fhirEndpoint };
    }
    onSuccess(authData, code) {
        this.state = { state: FacilitatedFHIRAuthState.SUCCESS, key: authData.key, code, authData, fhirEndpoint: authData.fhirEndpoint };
    }
    getFacilitatedFHIRAuthData() {
        return this.persistence.getFacilitatedFHIRAuthData();
    }
    setFacilitatedFHIRAuthData(authData) {
        this.persistence.setFacilitatedFHIRAuthData(authData);
    }
    resetAuthStateIfNeeded() {
        const authData = this.getFacilitatedFHIRAuthData();
        if (authData) {
            this.setFacilitatedFHIRAuthData(null);
            this.onError(FacilitatedFHIRAuthErrorReason.AUTH_FAILED, authData.key, authData.fhirEndpoint);
        }
    }
}
__decorate([
    observable
], FacilitatedFHIRAuthManager.prototype, "state", void 0);
__decorate([
    computed
], FacilitatedFHIRAuthManager.prototype, "hasCompletedAuth", null);
